import { Component } from "@theme/component";
import {
  fetchConfig,
  debounce,
  onAnimationEnd,
  prefersReducedMotion,
  resetShimmer,
} from "@theme/utilities";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import { ThemeEvents, QuantitySelectorUpdateEvent } from "@theme/events";
import { cartPerformance } from "@theme/performance";
import {
  createViewEventElement,
  CartErrorEvent,
  CartDiscountUpdateEvent,
  CartLinesUpdateEvent,
  CartNoteUpdateEvent,
  StandardEvents,
} from "@shopify/events";

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
export class CartItemsComponent extends createViewEventElement(Component) {
  #debouncedOnChange = debounce(
    /** @param {Event} event */
    (event) => {
      if (event instanceof QuantitySelectorUpdateEvent)
        this.#onQuantityChange(event);
    },
    150,
  );
  /** @type {Promise<any> | null} */
  #pendingCartFetch = null;

  /** @type {Promise<void>} */
  #cartUpdateQueue = Promise.resolve();

  /**
   * True when the event was dispatched from outside this cart-items-component (e.g.
   * `Shopify.actions.updateCart(...)` from an external app, or the SFAPI default
   * handler). Internal dispatchers (cart-discount-component, cart-note) live inside
   * `this` and either morph the section themselves or don't need a refresh — running
   * a fallback render in that case double-renders and can clobber form state.
   * @param {Event} event
   */
  #isExternalCartUpdate(event) {
    return !(event.target instanceof Node) || !this.contains(event.target);
  }

  /** @param {CartDiscountUpdateEvent} event */
  #handleDiscountUpdate = (event) => {
    const external = this.#isExternalCartUpdate(event);
    event.promise
      ?.then(({ detail }) => {
        const sectionsHtml = detail?.sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, {
            mode: this.isDrawer ? "hydration" : "full",
          });
          this.#updateCartQuantitySelectorButtonStates();
        } else if (external) {
          // External caller (Shopify.actions.updateCart or SFAPI default handler) didn't
          // attach sections; refetch so the discount UI reflects the post-mutation cart.
          // Internal cart-discount-component morphs the section itself — no fallback needed.
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? "hydration" : "full",
          });
        }
      })
      .catch((error) => {
        if (error?.name !== "AbortError")
          console.warn("[cart-items] Event promise rejected:", error);
      });
  };

  /** @param {CartNoteUpdateEvent} event */
  #handleNoteUpdate = (event) => {
    // Internal cart-note dispatches don't need a section refresh — the user typed the
    // value and the textarea retains it. Only external callers need the UI synced.
    if (!this.#isExternalCartUpdate(event)) return;
    event.promise
      ?.then(({ detail }) => {
        const sections = /** @type {Record<string, string> | undefined} */ (
          detail?.sections
        );
        const sectionsHtml = sections?.[this.sectionId];
        if (sectionsHtml) {
          morphSection(this.sectionId, sectionsHtml, {
            mode: this.isDrawer ? "hydration" : "full",
          });
        } else {
          sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            mode: this.isDrawer ? "hydration" : "full",
          });
        }
      })
      .catch((error) => {
        if (error?.name !== "AbortError")
          console.warn("[cart-items] Event promise rejected:", error);
      });
  };

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartUpdate,
    );
    document.addEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );
    document.addEventListener(
      StandardEvents.cartDiscountUpdate,
      this.#handleDiscountUpdate,
    );
    document.addEventListener(
      StandardEvents.cartNoteUpdate,
      this.#handleNoteUpdate,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(
      StandardEvents.cartLinesUpdate,
      this.#handleCartUpdate,
    );
    document.removeEventListener(
      ThemeEvents.quantitySelectorUpdate,
      this.#debouncedOnChange,
    );
    document.removeEventListener(
      StandardEvents.cartDiscountUpdate,
      this.#handleDiscountUpdate,
    );
    document.removeEventListener(
      StandardEvents.cartNoteUpdate,
      this.#handleNoteUpdate,
    );
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      lineKey: this.#lineKeyForIndex(line),
      quantity,
      action: "change",
    });
    const lineItemRow = this.#getCartItemRows()[line - 1];

    if (!lineItemRow) return;

    const textComponent = /** @type {TextComponent | undefined} */ (
      lineItemRow.querySelector("text-component")
    );
    textComponent?.shimmer();
  }

  /**
   * Cart line rows; falls back to DOM query when refs haven't synced yet.
   * @returns {HTMLTableRowElement[]}
   */
  #getCartItemRows() {
    const rows = this.refs.cartItemRows;
    if (rows?.length) return rows;

    return Array.from(
      this.querySelectorAll("tr.cart-items__table-row[data-key]"),
    );
  }

  /**
   * @param {number} line
   * @returns {string}
   */
  #lineKeyForIndex(line) {
    return this.#getCartItemRows()[line - 1]?.dataset.key ?? "";
  }

  /**
   * Resolves the current 1-based line index from a stable cart line key.
   * @param {{ line: number, lineKey?: string }} config
   * @returns {number | null}
   */
  #resolveLineIndex(config) {
    const rows = this.#getCartItemRows();

    if (config.lineKey) {
      const index = rows.findIndex((row) => row.dataset.key === config.lineKey);
      if (index >= 0) return index + 1;
    }

    const row = rows[config.line - 1];
    return row ? config.line : null;
  }

  /**
   * Applies updated section HTML after a cart mutation.
   * @param {Record<string, string | undefined>} sections
   * @param {number} itemCount
   * @param {boolean} isRemove
   */
  async #applyCartSectionUpdate(sections, itemCount, isRemove) {
    const sectionHtml = sections?.[this.sectionId];
    const cartPageUrl = new URL(
      Theme.routes?.cart_url || "/cart",
      window.location.origin,
    );

    if (!this.isDrawer) {
      await sectionRenderer.renderSection(this.sectionId, {
        cache: false,
        mode: "full",
        url: cartPageUrl,
      });

      const drawerHtml = sections?.["cart-drawer-section"];
      if (drawerHtml) {
        try {
          await morphSection("cart-drawer-section", drawerHtml, {
            mode: "hydration",
          });
        } catch {
          await sectionRenderer.renderSection("cart-drawer-section", {
            cache: false,
            mode: "full",
            injectStylesheet: true,
          });
        }
      }

      return;
    }

    if (itemCount === 0 || isRemove || !sectionHtml) {
      await sectionRenderer.renderSection(this.sectionId, {
        cache: false,
        mode: "full",
        injectStylesheet: true,
      });
      return;
    }

    await morphSection(this.sectionId, sectionHtml, { mode: "hydration" });
  }

  /**
   * Reads item count from a section rendering response fragment.
   * @param {Record<string, string | undefined>} sections
   * @returns {number}
   */
  #readItemCountFromSections(sections) {
    const sectionHtml = sections?.[this.sectionId];
    if (!sectionHtml) return -1;

    const parsedSection = new DOMParser().parseFromString(
      sectionHtml,
      "text/html",
    );
    const countText = parsedSection.querySelector(
      '[ref="cartItemCount"]',
    )?.textContent;
    return countText ? parseInt(countText, 10) : 0;
  }

  /**
   * Re-syncs the cart section from the server after a failed mutation.
   */
  #restoreCartSection() {
    const renderOptions = {
      cache: false,
      mode: "full",
      injectStylesheet: this.isDrawer,
    };

    if (!this.isDrawer) {
      renderOptions.url = new URL(
        Theme.routes?.cart_url || "/cart",
        window.location.origin,
      );
    }

    return sectionRenderer.renderSection(this.sectionId, renderOptions);
  }

  /**
   * Shows updating state on a single line without blocking the whole cart.
   * @param {number} line
   * @param {boolean} isUpdating
   */
  #setLineUpdating(line, isUpdating) {
    const row = this.#getCartItemRows()[line - 1];
    if (row instanceof HTMLElement) {
      row.classList.toggle("cart-items__table-row--updating", isUpdating);
      row.setAttribute("aria-busy", isUpdating ? "true" : "false");
      const removeButton = row.querySelector(".cart-items__remove");
      if (removeButton instanceof HTMLButtonElement) {
        removeButton.disabled = isUpdating;
      }
    }

    this.#setQuantitySelectorLoading(line, isUpdating);
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    const lineKey = this.#lineKeyForIndex(line);
    this.#setLineUpdating(line, true);

    this.updateQuantity({
      line,
      lineKey,
      quantity: 0,
      action: "clear",
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    this.#cartUpdateQueue = this.#cartUpdateQueue
      .then(() => this.#performCartUpdate(config))
      .catch((error) => {
        console.error(error);
      });

    return this.#cartUpdateQueue;
  }

  /**
   * @param {Object} config
   * @param {number} config.line
   * @param {string} [config.lineKey]
   * @param {number} config.quantity
   * @param {string} config.action
   * @returns {Promise<void>}
   */
  #performCartUpdate(config) {
    const resolvedLine = this.#resolveLineIndex(config);
    if (!resolvedLine) {
      return Promise.resolve();
    }

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
      `${config.action}:user-action`,
    );

    const { quantity } = config;
    const { cartTotal } = this.refs;
    const isRemove = config.action === "clear" || quantity === 0;

    if (isRemove) {
      this.#setLineUpdating(resolvedLine, true);
    } else {
      this.#disableCartItems();
    }

    const cartItemsComponents = document.querySelectorAll(
      "cart-items-component",
    );
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const body = JSON.stringify({
      line: resolvedLine,
      quantity: quantity,
      sections: Array.from(sectionsToUpdate).join(","),
      sections_url: this.isDrawer
        ? window.location.pathname
        : Theme.routes?.cart_url || "/cart",
    });

    cartTotal?.shimmer();

    const deferredUpdatePromise = CartLinesUpdateEvent.createPromise();
    const lineId =
      config.lineKey ||
      this.#getCartItemRows()[resolvedLine - 1]?.dataset.key ||
      "";
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action:
          config.action === "change" && quantity > 0 ? "update" : "remove",
        context: "cart",
        lines: [{ id: lineId, quantity }],
        promise: deferredUpdatePromise.promise,
      }),
    );

    return fetch(
      `${Theme.routes.cart_change_url}`,
      fetchConfig("json", { body }),
    )
      .then((response) => {
        return response.text();
      })
      .then(async (responseText) => {
        const parsedResponseText = JSON.parse(responseText);

        resetShimmer(this);

        if (parsedResponseText.errors) {
          this.#handleCartError(parsedResponseText);
          deferredUpdatePromise.reject(new Error(parsedResponseText.errors));
          return;
        }

        const sections = parsedResponseText.sections ?? {};
        const newCartItemCount = this.#readItemCountFromSections(sections);
        const itemCount =
          newCartItemCount >= 0
            ? newCartItemCount
            : (parsedResponseText.item_count ?? 0);

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        const eventDetail = {
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(
            parsedResponseText,
          ),
          detail: {
            sections,
            items: parsedResponseText.items,
            itemCount,
            source: "cart-items-component",
            didError: false,
          },
        };

        deferredUpdatePromise.resolve(eventDetail);

        await this.#applyCartSectionUpdate(sections, itemCount, isRemove);

        if (this.isConnected) {
          this.#updateCartQuantitySelectorButtonStates();
        }
      })
      .catch((error) => {
        deferredUpdatePromise.reject(error);

        this.#restoreCartSection().catch((restoreError) => {
          console.warn(
            "[cart-items] Failed to restore cart section:",
            restoreError,
          );
        });

        this.dispatchEvent(
          new CartErrorEvent({
            error: error?.message || "Failed to update cart",
            code: "SERVICE_UNAVAILABLE",
          }),
        );
      })
      .finally(() => {
        if (this.isConnected) {
          this.#setLineUpdating(resolvedLine, false);
          this.#enableCartItems();
        }

        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string} parsedResponseText.errors - The errors.
   */
  #handleCartError = (parsedResponseText) => {
    this.#restoreCartSection().catch((error) => {
      console.warn("[cart-items] Failed to restore cart section:", error);
    });

    this.dispatchEvent(
      new CartErrorEvent({
        error: parsedResponseText.errors || "Cart update failed",
        code: "INVALID",
      }),
    );
  };

  /**
   * Handles the cart update.
   *
   * @param {CartLinesUpdateEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.target === this) return;

    event.promise
      ?.then(async ({ detail }) => {
        // The originating cart-items-component already refreshed sections.
        if (detail?.source === "cart-items-component") return;

        const sections = detail?.sections;
        const cartItemsHtml = sections?.[this.sectionId];
        // Animate empty → non-empty in the drawer (possible in squeeze mode
        // where the page is interactive alongside the open drawer). This also
        // needs the response stylesheet because it adds the cart summary markup.
        const wasEmptyCartDrawer =
          this.isDrawer &&
          (this.querySelector("[data-cart-drawer-empty]") !== null ||
            this.closest(".cart-drawer--empty") !== null);

        // cart-drawer-component owns the empty → filled full section re-render.
        // Skip here to avoid racing that refresh with a hydration morph.
        if (wasEmptyCartDrawer && event.action === "add") {
          return;
        }

        /** @type {'hydration' | 'full'} */
        const mode =
          this.isDrawer && !wasEmptyCartDrawer ? "hydration" : "full";
        const morphOptions = {
          mode,
          injectStylesheet: wasEmptyCartDrawer,
        };

        if (cartItemsHtml) {
          const existingKeys = new Set(
            this.#getCartItemRows().map((row) => row.dataset.key),
          );

          if (this.isDrawer) {
            await morphSection(this.sectionId, cartItemsHtml, morphOptions);
          } else {
            await sectionRenderer.renderSection(this.sectionId, {
              cache: false,
              mode: "full",
              url: new URL(
                Theme.routes?.cart_url || "/cart",
                window.location.origin,
              ),
            });
          }

          // Animate newly added rows (reverse of the remove animation).
          if (
            this.isConnected &&
            !wasEmptyCartDrawer &&
            !prefersReducedMotion()
          ) {
            for (const row of this.#getCartItemRows()) {
              if (!existingKeys.has(row.dataset.key)) {
                row.classList.add("adding");
                onAnimationEnd(row, () => row.classList.remove("adding"));
              }
            }
          }

          // Update button states for all cart quantity selectors after morph
          this.#updateCartQuantitySelectorButtonStates();
        } else {
          await sectionRenderer.renderSection(this.sectionId, {
            cache: false,
            ...morphOptions,
          });
        }
      })
      .catch((error) => {
        if (error?.name !== "AbortError")
          console.warn("[cart-items] Event promise rejected:", error);
      });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add("cart-items-disabled");
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove("cart-items-disabled");
    this.#clearQuantitySelectorLoading();
  }

  /**
   * Sets loading state on a cart quantity selector for the given line.
   * @param {number} line
   * @param {boolean} isLoading
   */
  #setQuantitySelectorLoading(line, isLoading) {
    const quantitySelector = this.refs.quantitySelectors?.[line - 1];
    if (
      quantitySelector &&
      "setLoading" in quantitySelector &&
      typeof quantitySelector.setLoading === "function"
    ) {
      quantitySelector.setLoading(isLoading);
    }
  }

  /**
   * Clears loading state on all cart quantity selectors.
   */
  #clearQuantitySelectorLoading() {
    for (const selector of this.refs.quantitySelectors ?? []) {
      if (
        "setLoading" in selector &&
        typeof selector.setLoading === "function"
      ) {
        selector.setLoading(false);
      }
    }
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    const items = updatedCart.items ?? [];
    const quantityByVariant = new Map(
      items.map((item) => [item.variant_id.toString(), item.quantity]),
    );

    for (const selector of document.querySelectorAll(
      "quantity-selector-component[data-variant-id]",
    )) {
      const variantId = selector.getAttribute("data-variant-id");
      if (!variantId) continue;

      const quantity = quantityByVariant.get(variantId) ?? 0;
      const input = selector.querySelector("input[data-cart-quantity]");
      if (!input) continue;

      input.setAttribute("data-cart-quantity", quantity.toString());

      if (
        "updateCartQuantity" in selector &&
        typeof selector.updateCartQuantity === "function"
      ) {
        selector.updateCartQuantity();
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll(
      "cart-quantity-selector-component",
    )) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  async fetchCartData() {
    if (this.#pendingCartFetch) return this.#pendingCartFetch;

    this.#pendingCartFetch = (async () => {
      const response = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      });
      if (!response.ok)
        throw new Error(
          `Failed to fetch cart: ${response.status} ${response.statusText}`,
        );
      const data = await response.json();
      return data;
    })().finally(() => {
      this.#pendingCartFetch = null;
    });

    return this.#pendingCartFetch;
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error("Section id missing");

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get("cart-items-component")) {
  customElements.define("cart-items-component", CartItemsComponent);
}
